import { __decorate } from "tslib";
import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { action, computed, isArrayLike, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import isString from 'lodash/isString';
import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import { getConfig } from 'choerodon-ui/lib/configure';
import { TextField } from '../text-field/TextField';
import autobind from '../_util/autobind';
import keepRunning from '../_util/keepRunning';
import Icon from '../icon';
import { getNearStepValues, MAX_SAFE_INTEGER, MIN_SAFE_INTEGER, parseNumber, plus } from './utils';
import isEmpty from '../_util/isEmpty';
import { $l } from '../locale-context';
import { FieldType } from '../data-set/enum';
import defaultFormatNumber from '../formatter/formatNumber';
import localeContext from '../locale-context/LocaleContext';
import { getNumberFormatOptions, getNumberFormatter } from '../field/utils';
function getCurrentValidValue(value) {
    return Number(value.replace(/\.$/, '')) || 0;
}
export class NumberField extends TextField {
    get lang() {
        const { lang } = this.observableProps;
        if (lang) {
            return lang;
        }
        const { dataSet } = this;
        if (dataSet && dataSet.lang) {
            return dataSet.lang;
        }
        const { numberFormatLanguage, locale } = localeContext;
        if (numberFormatLanguage) {
            return numberFormatLanguage;
        }
        return locale.lang;
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('NumberField', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
        };
    }
    get allowDecimal() {
        const { min, nonStrictStep } = this;
        const precision = this.getProp('precision');
        if (precision === 0) {
            return false;
        }
        // 非严格步距下允许输入小数
        if (nonStrictStep) {
            return true;
        }
        const step = this.getProp('step');
        return !step || step % 1 !== 0 || (!!min && min % 1 !== 0);
    }
    get nonStrictStep() {
        const nonStrictStep = this.getProp('nonStrictStep');
        if (nonStrictStep !== undefined) {
            return nonStrictStep;
        }
        const numberFieldNonStrictStep = getConfig('numberFieldNonStrictStep');
        if (numberFieldNonStrictStep !== undefined) {
            return numberFieldNonStrictStep;
        }
        return false;
    }
    get allowNegative() {
        const { min } = this;
        return isNil(min) || min < 0;
    }
    get min() {
        return this.getLimit('min');
    }
    get max() {
        return this.getLimit('max');
    }
    get value() {
        const { value } = this.observableProps;
        if (isArrayLike(value)) {
            return value;
        }
        const { range } = this;
        if (isArrayLike(range)) {
            if (isPlainObject(value)) {
                const [start, end] = range;
                return {
                    [start]: value[start],
                    [end]: value[end],
                };
            }
        }
        return value;
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    savePlusRef(ref) {
        this.plusElement = ref;
    }
    saveMinusRef(ref) {
        this.minusElement = ref;
    }
    isLowerRange(value1, value2) {
        return value1 < value2;
    }
    getFieldType() {
        return FieldType.number;
    }
    getLimit(type) {
        const { record } = this;
        const limit = this.getProp(type);
        if (record && isString(limit)) {
            const num = record.get(limit);
            if (num !== undefined) {
                return num;
            }
        }
        return limit;
    }
    getValidatorProps() {
        const { min, max } = this;
        const step = this.getProp('step');
        const nonStrictStep = this.nonStrictStep;
        return omit({
            ...super.getValidatorProps(),
            min,
            max,
            step,
            nonStrictStep,
        }, [
            'maxLength',
            'minLength',
        ]);
    }
    getInnerSpanButton() {
        const { prefixCls, range } = this;
        const { longPressPlus } = this.props;
        const step = this.getProp('step');
        if (step && !range && !this.readOnly && !this.disabled) {
            const plusIconProps = {
                ref: this.savePlusRef,
                key: 'plus',
                className: `${prefixCls}-plus`,
                onMouseDown: longPressPlus ? this.handlePlus : this.handleOncePlus,
                onTouchStart: this.handleOncePlus,
            };
            const minIconProps = {
                ref: this.saveMinusRef,
                key: 'minus',
                className: `${prefixCls}-minus`,
                onMouseDown: longPressPlus ? this.handleMinus : this.handleOnceMinus,
                onTouchStart: this.handleOnceMinus,
            };
            return this.wrapperInnerSpanButton(React.createElement("div", null,
                React.createElement("div", Object.assign({}, plusIconProps),
                    React.createElement(Icon, { type: "keyboard_arrow_up" })),
                React.createElement("div", Object.assign({}, minIconProps),
                    React.createElement(Icon, { type: "keyboard_arrow_down" }))));
        }
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        const suffix = this.getSuffix();
        const button = this.getInnerSpanButton();
        return super.getWrapperClassNames({
            [`${prefixCls}-step-suffix`]: button && isValidElement(suffix),
            ...args,
        });
    }
    handleEnterDown(e) {
        if (this.multiple && this.range && this.text) {
            this.prepareSetValue(this.text);
        }
        super.handleEnterDown(e);
        if (this.multiple && this.range) {
            this.setRangeTarget(0);
            this.beginRange();
        }
    }
    handleKeyDown(e) {
        if (!this.disabled && !this.readOnly) {
            switch (e.keyCode) {
                case KeyCode.UP:
                    this.handleKeyDownUp(e);
                    break;
                case KeyCode.DOWN:
                    this.handleKeyDownDown(e);
                    break;
                default:
            }
        }
        super.handleKeyDown(e);
    }
    handleKeyDownUp(e) {
        e.preventDefault();
        if (this.getProp('step')) {
            this.step(true);
        }
    }
    handleKeyDownDown(e) {
        e.preventDefault();
        if (this.getProp('step')) {
            this.step(false);
        }
    }
    handlePlus(_e, isKeeping) {
        this.step(true, isKeeping);
    }
    handleMinus(_e, isKeeping) {
        this.step(false, isKeeping);
    }
    handleOncePlus() {
        this.step(true);
    }
    handleOnceMinus() {
        this.step(false);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'nonStrictStep',
            'formatter',
            'formatterOptions',
            'longPressPlus',
            'precision',
            'numberGrouping',
            'maxLength',
        ]);
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            max: props.max,
            min: props.min,
            precision: props.precision,
            nonStrictStep: props.nonStrictStep,
        };
    }
    step(isPlus, isKeeping) {
        const min = defaultTo(this.min, -MAX_SAFE_INTEGER);
        const max = defaultTo(this.max, MAX_SAFE_INTEGER);
        const step = defaultTo(this.getProp('step'), 1);
        const { nonStrictStep } = this;
        // 需要处理非严格模式
        let newValue;
        const value = this.multiple || this.isFocused ? Number(this.text || this.getValue()) : this.getValue();
        const currentValue = getCurrentValidValue(String(value));
        newValue = currentValue;
        const nearStep = getNearStepValues(currentValue, step, min, max);
        if (nonStrictStep === false && nearStep) {
            switch (nearStep.length) {
                case 1:
                    newValue = nearStep[0];
                    break;
                case 2:
                    newValue = nearStep[isPlus ? 1 : 0];
                    break;
                default:
            }
        }
        else {
            const nextValue = plus(currentValue, (isPlus ? step : -step));
            if (nextValue < min) {
                newValue = min;
            }
            else if (nextValue > max) {
                const nearMaxStep = getNearStepValues(max, step, min, max);
                if (nearMaxStep) {
                    newValue = nearMaxStep[0];
                }
                else {
                    newValue = max;
                }
            }
            else {
                newValue = nextValue;
            }
        }
        // 不要进行对比操作,在table中使用的时候,因为NumberField会作为editor使用,所以在 对第一个cell只点击一次的情况下(例如plus)
        // 此时切换到第二个cell进行编辑，无法进行上次操作(同上次的plus)
        // this.value !== newValue
        if (this.multiple || isKeeping) {
            this.setText(String(newValue));
        }
        else {
            this.prepareSetValue(newValue);
        }
    }
    prepareSetValue(value) {
        super.prepareSetValue(isNaN(value) || isEmpty(value) ? null : parseNumber(value, this.getProp('precision')));
    }
    restrictInput(value) {
        if (value) {
            let restrict = '0-9';
            if (this.allowDecimal) {
                restrict += '.';
            }
            const isNegative = this.allowNegative && /^-/.test(value);
            value = super.restrictInput(value.replace(new RegExp(`[^${restrict}]+`, 'g'), ''));
            const values = value.split('.');
            if (values.length > 2) {
                value = `${values.shift()}.${values.join('')}`;
            }
            if (isNegative) {
                value = `-${value}`;
            }
        }
        return value;
    }
    getFormatOptions(value) {
        return getNumberFormatOptions((name) => this.getProp(name), () => this.getValue(), value, this.lang);
    }
    getFormatter() {
        return this.getProp('formatter') || getNumberFormatter();
    }
    processText(value) {
        const formatOptions = this.getFormatOptions(Number(value));
        return this.getFormatter()(value, formatOptions.lang, formatOptions.options);
    }
    renderLengthInfo() {
        return undefined;
    }
}
NumberField.displayName = 'NumberField';
NumberField.propTypes = {
    /**
     * 最小值
     */
    min: PropTypes.number,
    /**
     * 最大值
     */
    max: PropTypes.number,
    /**
     * 步距
     */
    step: PropTypes.number,
    /**
     * 非严格步距
     */
    nonStrictStep: PropTypes.bool,
    /**
     * 格式器
     */
    formatter: PropTypes.func,
    /**
     * 格式器参数
     */
    longPressPlus: PropTypes.bool,
    /**
     * 是否开启长按步距增加
     */
    formatterOptions: PropTypes.object,
    ...TextField.propTypes,
};
NumberField.defaultProps = {
    ...TextField.defaultProps,
    suffixCls: 'input-number',
    longPressPlus: true,
    max: MAX_SAFE_INTEGER,
    min: MIN_SAFE_INTEGER,
};
NumberField.format = defaultFormatNumber;
__decorate([
    computed
], NumberField.prototype, "lang", null);
__decorate([
    computed
], NumberField.prototype, "allowDecimal", null);
__decorate([
    computed
], NumberField.prototype, "nonStrictStep", null);
__decorate([
    computed
], NumberField.prototype, "allowNegative", null);
__decorate([
    computed
], NumberField.prototype, "min", null);
__decorate([
    computed
], NumberField.prototype, "max", null);
__decorate([
    computed
], NumberField.prototype, "value", null);
__decorate([
    autobind
], NumberField.prototype, "savePlusRef", null);
__decorate([
    autobind
], NumberField.prototype, "saveMinusRef", null);
__decorate([
    action
], NumberField.prototype, "handleEnterDown", null);
__decorate([
    autobind
], NumberField.prototype, "handleKeyDown", null);
__decorate([
    keepRunning(function () {
        return this.plusElement;
    })
], NumberField.prototype, "handlePlus", null);
__decorate([
    keepRunning(function () {
        return this.minusElement;
    })
], NumberField.prototype, "handleMinus", null);
__decorate([
    autobind
], NumberField.prototype, "handleOncePlus", null);
__decorate([
    autobind
], NumberField.prototype, "handleOnceMinus", null);
let ObserverNumberField = class ObserverNumberField extends NumberField {
};
ObserverNumberField.defaultProps = NumberField.defaultProps;
ObserverNumberField.format = defaultFormatNumber;
ObserverNumberField = __decorate([
    observer
], ObserverNumberField);
export default ObserverNumberField;
